import React from "react";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, CircleUser, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useGetHomeQuery, usePutHomeMutation } from "@/store/rest_api";
import {
  hookFormValidatePassword,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS_HINT,
} from "@/util/validation";
import { apiErrorMessage } from "@/util/apiError";
import type User from "@/model/interface/User";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TwoFactorSection from "./TwoFactorSection";
import TrustedDevicesSection from "./TrustedDevicesSection";
import LinkedSignInMethods from "./LinkedSignInMethods";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

interface ProfileFormValues {
  firstName: string;
  lastName: string;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function AccountSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab") || "profile";
  const activeTab = requestedTab === "password" || requestedTab === "security" ? "security" : "profile";

  const { data: user, isLoading } = useGetHomeQuery();
  const [putProfile, { isLoading: isSavingProfile }] = usePutHomeMutation();
  const [putPassword, { isLoading: isSavingPassword }] = usePutHomeMutation();

  const profileForm = useForm<ProfileFormValues>({
    values: { firstName: user?.firstName ?? "", lastName: user?.lastName ?? "" },
  });
  const passwordForm = useForm<PasswordFormValues>();
  const newPassword = passwordForm.watch("newPassword");

  const onSelectTab = (tab: string) => {
    setSearchParams(tab === "profile" ? {} : { tab });
  };

  const onSaveProfile = async (data: ProfileFormValues) => {
    if (!user?.id) {
      return;
    }
    const payload: User = {
      id: user.id,
      email: user.email,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
    };
    try {
      await putProfile(payload).unwrap();
      toast.success("Profile updated");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update profile"));
    }
  };

  const onChangePassword = async (data: PasswordFormValues) => {
    if (!user?.id) {
      return;
    }
    const payload: User = {
      id: user.id,
      email: user.email,
      currentPassword: data.currentPassword,
      password: data.newPassword,
      passwordConfirmation: data.confirmPassword,
    };
    try {
      await putPassword(payload).unwrap();
      toast.success("Password updated");
      passwordForm.reset({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to update password"));
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-foreground">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-64 w-full max-w-2xl" />
      </div>
    );
  }

  return (
    <div className="p-4 text-foreground">
      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Account settings</h1>
        <p className="text-muted-foreground text-sm">
          Your personal details, security and sign in. The same in every organisation you belong to.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={onSelectTab}>
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>Profile</CardTitle>
              <CardDescription>Your name and email address.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={profileForm.handleSubmit(onSaveProfile)} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">First name</Label>
                    <Input
                      id="firstName"
                      {...profileForm.register("firstName", { required: true, minLength: 2, maxLength: 50 })}
                    />
                    {profileForm.formState.errors.firstName && (
                      <p className="text-xs text-destructive">First name is required</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Last name</Label>
                    <Input
                      id="lastName"
                      {...profileForm.register("lastName", { required: true, minLength: 2, maxLength: 50 })}
                    />
                    {profileForm.formState.errors.lastName && (
                      <p className="text-xs text-destructive">Last name is required</p>
                    )}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={user?.email ?? ""} disabled readOnly className="bg-muted" />
                  <p className="text-xs text-muted-foreground">Contact support to change your email address.</p>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={isSavingProfile || !profileForm.formState.isDirty}>
                    {isSavingProfile && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Link
            to="/my-settings"
            className="mt-6 flex max-w-2xl items-center gap-3 rounded-lg border bg-muted/40 p-4 no-underline"
          >
            <CircleUser className="h-4.5 w-4.5 shrink-0 text-primary" />
            <span className="flex-1">
              <span className="block text-sm font-semibold">Your settings in this organisation</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                Consent warnings, voicemail greeting and notifications.
              </span>
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Link>
        </TabsContent>

        <TabsContent value="security" className="mt-6 space-y-6">
          <TwoFactorSection />
          <TrustedDevicesSection />
          <LinkedSignInMethods />

          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle>Password</CardTitle>
              <CardDescription>Choose a strong password you don't use elsewhere.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={passwordForm.handleSubmit(onChangePassword)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="currentPassword">Current password</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    autoComplete="current-password"
                    {...passwordForm.register("currentPassword", { required: true })}
                  />
                  {passwordForm.formState.errors.currentPassword && (
                    <p className="text-xs text-destructive">Current password is required</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="newPassword">New password</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    autoComplete="new-password"
                    {...passwordForm.register("newPassword", {
                      required: true,
                      validate: hookFormValidatePassword({ minChars: PASSWORD_MIN_LENGTH }),
                    })}
                  />
                  {passwordForm.formState.errors.newPassword ? (
                    <p className="text-xs text-destructive">
                      {(passwordForm.formState.errors.newPassword.message as string) || PASSWORD_REQUIREMENTS_HINT}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">{PASSWORD_REQUIREMENTS_HINT}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword">Confirm new password</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    {...passwordForm.register("confirmPassword", {
                      required: true,
                      validate: (value) => value === newPassword || "Passwords do not match",
                    })}
                  />
                  {passwordForm.formState.errors.confirmPassword && (
                    <p className="text-xs text-destructive">
                      {(passwordForm.formState.errors.confirmPassword.message as string) || "Passwords do not match"}
                    </p>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={isSavingPassword}>
                    {isSavingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Update password
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
